import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X as XIcon, ArrowLeft, ArrowRight, Copy } from "lucide-react";

const ROLE_LABELS = { doctor: "Dr", nurse: "N", receptionist: "R", lab_tech: "Lab", radiologist: "Img", pharmacist: "Rx", cashier: "Cash" };
const SHIFTS = ["Day", "Night"];

function fmtShort(iso) {
  try { return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", day: "2-digit", month: "short" }); }
  catch { return iso; }
}
function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00"); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10);
}
function startOfWeek() {
  const d = new Date(); d.setDate(d.getDate() - d.getDay()); // Sunday
  return d.toISOString().slice(0, 10);
}

export function WeeklyRosterView({ onExit }) {
  const [start, setStart] = useState(startOfWeek());
  const [grid, setGrid] = useState({});
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drag, setDrag] = useState(null); // {role, user, sourceDate, sourceShift}
  const [dragOver, setDragOver] = useState(null); // "date|shift"

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/roster/week?start_date=${start}`);
      setGrid(data.grid || {}); setDates(data.dates || []);
    } catch { toast.error("Failed to load weekly roster"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [start]); // eslint-disable-line

  const cell = (date, shift) => grid[date]?.[shift];
  const pins = (date, shift) => {
    const c = cell(date, shift); if (!c) return [];
    const list = [];
    Object.entries(c.assignments || {}).forEach(([role, arr]) => {
      (arr || []).forEach((u) => list.push({ ...u, role }));
    });
    return list;
  };

  const saveCell = async (date, shift, updater) => {
    const c = cell(date, shift) || { targets: {}, assignments: {}, notes: null };
    const next = updater(structuredClone({ targets: c.targets || {}, assignments: c.assignments || {}, notes: c.notes || null }));
    // Convert assignments {role:[{user_id,name}]} -> {role:[user_id]}
    const assignPayload = {};
    Object.entries(next.assignments || {}).forEach(([r, arr]) => { assignPayload[r] = (arr || []).map((u) => u.user_id); });
    try {
      await api.post("/roster", { shift_date: date, shift_name: shift, targets: next.targets, assignments: assignPayload, notes: next.notes });
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const removePin = (date, shift, role, userId) => {
    saveCell(date, shift, (c) => {
      c.assignments = c.assignments || {};
      c.assignments[role] = (c.assignments[role] || []).filter((u) => u.user_id !== userId);
      return c;
    });
  };

  const movePin = async (from, to, user, role) => {
    if (from.date === to.date && from.shift === to.shift) return;
    // Remove from source
    const src = cell(from.date, from.shift);
    if (src) {
      const srcAssign = {};
      Object.entries(src.assignments || {}).forEach(([r, arr]) => {
        srcAssign[r] = (arr || []).filter((u) => !(r === role && u.user_id === user.user_id)).map((u) => u.user_id);
      });
      try { await api.post("/roster", { shift_date: from.date, shift_name: from.shift, targets: src.targets || {}, assignments: srcAssign, notes: src.notes || null }); }
      catch { toast.error("Move failed"); return; }
    }
    // Add to target
    const dst = cell(to.date, to.shift) || { targets: {}, assignments: {}, notes: null };
    const dstAssign = {};
    Object.entries(dst.assignments || {}).forEach(([r, arr]) => { dstAssign[r] = (arr || []).map((u) => u.user_id); });
    dstAssign[role] = [...new Set([...(dstAssign[role] || []), user.user_id])];
    try {
      await api.post("/roster", { shift_date: to.date, shift_name: to.shift, targets: dst.targets || {}, assignments: dstAssign, notes: dst.notes || null });
      toast.success(`Moved ${user.name}`);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Move failed"); }
  };

  const copyCell = async (from, to) => {
    const c = cell(from.date, from.shift); if (!c) return;
    const assignPayload = {};
    Object.entries(c.assignments || {}).forEach(([r, arr]) => { assignPayload[r] = (arr || []).map((u) => u.user_id); });
    try {
      await api.post("/roster", { shift_date: to.date, shift_name: to.shift, targets: c.targets || {}, assignments: assignPayload, notes: c.notes || null });
      toast.success(`Copied ${from.shift} ${fmtShort(from.date)} → ${to.shift} ${fmtShort(to.date)}`);
      await load();
    } catch (err) { toast.error("Copy failed"); }
  };

  return (
    <div className="p-5 space-y-4" data-testid="weekly-roster-view">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button data-testid="week-prev" onClick={() => setStart(addDays(start, -7))} className="h-8 w-8 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center"><ArrowLeft className="w-4 h-4" /></button>
          <div className="text-sm font-semibold">Week of {fmtShort(start)} → {fmtShort(addDays(start, 6))}</div>
          <button data-testid="week-next" onClick={() => setStart(addDays(start, 7))} className="h-8 w-8 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center justify-center"><ArrowRight className="w-4 h-4" /></button>
          <button data-testid="week-today" onClick={() => setStart(startOfWeek())} className="h-8 px-3 rounded-lg text-xs font-medium border border-slate-200 hover:bg-slate-50">This week</button>
        </div>
        <button data-testid="weekly-exit" onClick={onExit} className="h-8 px-3 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-100">← Back to daily view</button>
      </div>

      <div className="text-[11px] text-slate-500">Drag a staff chip to another cell to move them · click × to remove · use the Copy button to duplicate a shift into the next cell.</div>

      <div className="overflow-x-auto">
        <div className="grid gap-1" style={{ gridTemplateColumns: "80px repeat(7, minmax(140px, 1fr))" }}>
          <div />
          {dates.map((d) => (
            <div key={d} className="text-center text-[11px] uppercase tracking-wide text-slate-500 font-semibold py-2 border-b border-slate-200" data-testid={`week-col-${d}`}>{fmtShort(d)}</div>
          ))}
          {SHIFTS.map((shift) => (
            <React.Fragment key={shift}>
              <div className="flex items-center justify-end pr-2 text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{shift}</div>
              {dates.map((d) => {
                const c = cell(d, shift);
                const rolePins = pins(d, shift);
                const target = c?.targets || {};
                const totalTarget = Object.values(target).reduce((a, b) => a + (Number(b) || 0), 0);
                const over = dragOver === `${d}|${shift}`;
                return (
                  <div
                    key={d + shift}
                    data-testid={`week-cell-${d}-${shift}`}
                    onDragOver={(e) => { if (drag) { e.preventDefault(); setDragOver(`${d}|${shift}`); } }}
                    onDragLeave={() => setDragOver(null)}
                    onDrop={(e) => { e.preventDefault(); if (drag) { movePin({ date: drag.sourceDate, shift: drag.sourceShift }, { date: d, shift }, drag.user, drag.role); } setDrag(null); setDragOver(null); }}
                    className={`min-h-[100px] rounded-lg border p-2 text-xs bg-white ${over ? "border-[#1976D2] bg-blue-50" : c ? "border-slate-200" : "border-dashed border-slate-200"}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] font-semibold text-slate-500 tabular">{totalTarget || 0} tgt</span>
                      {c && dates.indexOf(d) < 6 && (
                        <button data-testid={`week-copy-${d}-${shift}`} onClick={() => copyCell({ date: d, shift }, { date: addDays(d, 1), shift })} title="Copy to next day" className="text-slate-400 hover:text-[#1565C0]"><Copy className="w-3 h-3" /></button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {rolePins.map((u) => (
                        <span
                          key={`${u.role}-${u.user_id}`}
                          draggable
                          onDragStart={() => setDrag({ role: u.role, user: { user_id: u.user_id, name: u.name }, sourceDate: d, sourceShift: shift })}
                          onDragEnd={() => { setDrag(null); setDragOver(null); }}
                          data-testid={`week-pin-${d}-${shift}-${u.user_id}`}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-[#1565C0] flex items-center gap-1 cursor-move"
                          title={`${u.role} · drag to move`}
                        >
                          <span className="font-semibold">{ROLE_LABELS[u.role] || u.role}</span>
                          {u.name.split(" ").slice(-1)[0]}
                          <button onClick={() => removePin(d, shift, u.role, u.user_id)} className="hover:text-[#B71C1C]"><XIcon className="w-2.5 h-2.5" /></button>
                        </span>
                      ))}
                      {!c && <span className="text-[10px] text-slate-400 italic">empty</span>}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
      {loading && <div className="text-xs text-slate-500">Loading…</div>}
    </div>
  );
}
